import { Theme } from '../types';
import playbookRaw from './connectionsPlaybook.json';

/**
 * D&S Finance Connections Playbook — distilled advisory content (2024, Karina).
 * Sourced from the 3 tier playbooks (Front Line / Managers-of-Managers / Org Leaders).
 * Used to ground the AI Insights summary: when a leader is flagged below-range on a
 * theme, the relevant best practices / actions for that theme + tier are surfaced.
 *
 * NOTE: the theme mapping is PROVISIONAL pending Karina's confirmation. `subTheme`
 * preserves the original legacy name so re-mapping is a data-only change.
 */

export type LeaderTier = 'FRONT_LINE' | 'MGR_OF_MGRS' | 'ORG_LEADER';

export interface PlaybookEntry {
  theme: Theme;
  tier: LeaderTier;
  subTheme: string;
  bestPractices: string[];
  recommendedActions: string[];
  pitfalls: string[];
  reflectionQuestions: string[];
  example?: string;
}

export const CONNECTIONS_PLAYBOOK: PlaybookEntry[] = playbookRaw as PlaybookEntry[];

/**
 * Map a leader's org span/depth to a playbook tier.
 * - FRONT_LINE: manages individual contributors (no manager reports)
 * - MGR_OF_MGRS: manages other managers, but not an org leader
 * - ORG_LEADER: L8+ / top-of-org in the current view
 */
export function tierForLeader(opts: { isOrgLeader?: boolean; managesManagers?: boolean }): LeaderTier {
  if (opts.isOrgLeader) return 'ORG_LEADER';
  if (opts.managesManagers) return 'MGR_OF_MGRS';
  return 'FRONT_LINE';
}

/** All playbook entries for a single theme + tier (usually 1, but future-proofed for many). */
export function getPlaybookEntries(theme: Theme, tier: LeaderTier): PlaybookEntry[] {
  return CONNECTIONS_PLAYBOOK.filter((e) => e.theme === theme && e.tier === tier);
}

/**
 * Retrieve the playbook slice for a set of flagged themes at a given tier.
 * This is what the payload-builder injects into the AI Insights prompt —
 * only the entries relevant to THIS leader's flagged themes, not the whole dataset.
 */
export function getPlaybookForThemes(themes: Theme[], tier: LeaderTier): PlaybookEntry[] {
  const wanted = new Set(themes);
  return CONNECTIONS_PLAYBOOK.filter((e) => e.tier === tier && wanted.has(e.theme));
}
